import { Alert, Button, Group, Loader, Modal, Progress, Stack, Text } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import { IconAlertCircle, IconCheck, IconClock, IconLoader2, IconX } from '@tabler/icons-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CloneSubtask,
  CloneTaskResponse,
  CloneTaskStatus,
  getActiveCloneTask,
  getCloneTask,
  retryCloneTask,
} from '@mygpt/app/apis/services/robots';

interface Props {
  opened: boolean;
  onClose: () => void;
  /** Either taskId or aiId must be provided. taskId takes precedence. */
  taskId?: string | null;
  aiId?: string | null;
}

const POLL_MS = 3000;
const MAX_RETRY = 3;

function statusIcon(status: CloneTaskStatus) {
  switch (status) {
    case 'success':
      return <IconCheck size={16} color="green" />;
    case 'running':
      return <IconLoader2 size={16} className="animate-spin" />;
    case 'pending':
      return <IconClock size={16} color="gray" />;
    case 'failed':
      return <IconX size={16} color="red" />;
  }
}

export default function CloneProgressModal({ opened, onClose, taskId, aiId }: Props) {
  const { t } = useTranslation();
  const [task, setTask] = useState<CloneTaskResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const resolvedTaskIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!opened) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      setTask(null);
      resolvedTaskIdRef.current = null;
      return;
    }

    let cancelled = false;
    setLoading(true);

    const fetchOnce = async () => {
      try {
        let res: CloneTaskResponse;
        if (resolvedTaskIdRef.current) {
          res = await getCloneTask(resolvedTaskIdRef.current);
        } else if (taskId) {
          res = await getCloneTask(taskId);
          resolvedTaskIdRef.current = res.id;
        } else if (aiId) {
          res = await getActiveCloneTask(aiId);
          resolvedTaskIdRef.current = res.id;
        } else {
          throw new Error('taskId or aiId required');
        }
        if (cancelled) return;
        setTask(res);
        if (res.status === 'success' || res.status === 'failed') {
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }
        }
      } catch (err: any) {
        if (cancelled) return;
        showNotification({ message: err?.message || t('clone_progress.load_failed'), color: 'red' });
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchOnce();
    intervalRef.current = setInterval(fetchOnce, POLL_MS);

    return () => {
      cancelled = true;
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [opened, taskId, aiId]);

  const handleRetry = async () => {
    if (!task) return;
    setRetrying(true);
    try {
      const res = await retryCloneTask(task.id);
      setTask(res);
      // restart polling if it had stopped
      if (!intervalRef.current) {
        intervalRef.current = setInterval(async () => {
          try {
            const next = await getCloneTask(res.id);
            setTask(next);
            if ((next.status === 'success' || next.status === 'failed') && intervalRef.current) {
              clearInterval(intervalRef.current);
              intervalRef.current = null;
            }
          } catch {
            // ignore
          }
        }, POLL_MS);
      }
      showNotification({ message: t('clone_progress.retry_started'), color: 'blue' });
    } catch (err: any) {
      showNotification({ message: err?.message || t('clone_progress.retry_failed'), color: 'red' });
    } finally {
      setRetrying(false);
    }
  };

  return (
    <Modal opened={opened} onClose={onClose} title={t('clone_progress.title')} size="lg" centered>
      {loading && !task ? (
        <Group justify="center" py="xl">
          <Loader />
        </Group>
      ) : !task ? (
        <Text c="gray.5">{t('clone_progress.no_task')}</Text>
      ) : (
        <Stack gap="md">
          <Text fw={500}>{task.new_robot_name}</Text>
          <Progress
            value={task.progress_percent}
            striped={task.status === 'running'}
            animated={task.status === 'running'}
          />
          <Text size="xs" c="gray.5">
            {task.progress_percent}%
          </Text>

          <Stack gap={4}>
            <Text fw={500} size="sm">
              {t('clone_progress.subtasks')}
            </Text>
            {task.subtasks.map((s: CloneSubtask, idx: number) => (
              <Group key={idx} gap="xs" wrap="nowrap">
                {statusIcon(s.status)}
                <Text size="sm" style={{ flex: 1 }}>
                  {s.kind === 'dataset_share' || s.kind === 'dataset_clone'
                    ? t(`clone_progress.subtask_kind.${s.kind}`, { name: s.label })
                    : t(`clone_progress.subtask_kind.${s.kind}`, { defaultValue: s.label })}
                </Text>
                <Text size="xs" c="gray.5">
                  {s.status === 'running'
                    ? t('clone_progress.subtask_status.running', { progress: s.progress })
                    : t(`clone_progress.subtask_status.${s.status}`)}
                </Text>
              </Group>
            ))}
          </Stack>

          {task.status === 'failed' && task.failed_reason && (
            <Alert icon={<IconAlertCircle size={16} />} color="red" title={t('clone_progress.failed_reason')}>
              {task.failed_reason}
            </Alert>
          )}

          <Text size="xs" c="gray.5">
            {t('clone_progress.retry_count', { count: task.retry_count, max: MAX_RETRY })}
          </Text>

          <Group justify="flex-end">
            <Button variant="outline" onClick={onClose}>
              {t('clone_progress.close')}
            </Button>
            {task.can_retry && (
              <Button color="orange" onClick={handleRetry} loading={retrying}>
                {t('clone_progress.retry')}
              </Button>
            )}
          </Group>
        </Stack>
      )}
    </Modal>
  );
}
